import { Controller } from "@hotwired/stimulus"

// Hides the sign-in card contents and shows the spinner when either form is submitted.
// Restores the contents when page is shown from bfcache (e.g. user hits back after failed login).
// Gates the email/password Sign In button on login + password + Turnstile.
export default class extends Controller {
  static targets = ["content", "spinner", "login", "password", "loginSubmit", "captchaResponse"]

  static POLL_INTERVAL = 400

  // Chrome withholds an autofilled credential's value from scripts until the user
  // interacts with the page, so a filled field can still read as "". The pseudo-class
  // matches regardless. :-webkit-autofill is the older spelling still used by Safari.
  static AUTOFILL_SELECTORS = [":autofill", ":-webkit-autofill"]

  connect() {
    this.boundPageshow = this.handlePageshow.bind(this)
    window.addEventListener("pageshow", this.boundPageshow)
    this.syncLoginSubmit()
    this.watchState()
  }

  disconnect() {
    window.removeEventListener("pageshow", this.boundPageshow)
    clearInterval(this.stateWatcher)
  }

  // Autofill and password managers change fields without firing input events.
  watchState() {
    if (!this.hasLoginSubmitTarget) return

    this.stateWatcher = setInterval(
      () => this.syncLoginSubmit(),
      this.constructor.POLL_INTERVAL
    )
  }

  captchaVerified() {
    this.syncLoginSubmit()
  }

  captchaCleared() {
    this.syncLoginSubmit()
  }

  sync() {
    this.syncLoginSubmit()
  }

  syncLoginSubmit() {
    if (!this.hasLoginSubmitTarget) return

    const loginFilled = !this.hasLoginTarget || this.filled(this.loginTarget)
    const passwordFilled = !this.hasPasswordTarget || this.filled(this.passwordTarget)
    this.loginSubmitTarget.disabled = !(this.captchaSolved && loginFilled && passwordFilled)
  }

  filled(field) {
    if (field.value.trim() !== "") return true

    return this.constructor.AUTOFILL_SELECTORS.some((selector) => {
      try {
        return field.matches(selector)
      } catch {
        return false
      }
    })
  }

  // Read the token rather than trust the verified event: a captcha that resolved before
  // this controller connected would otherwise leave the button stuck.
  get captchaSolved() {
    return this.hasCaptchaResponseTarget && this.captchaResponseTarget.value.trim() !== ""
  }

  showSpinner() {
    this.contentTargets.forEach((el) => el.classList.add("hidden"))
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("hidden")
    }
  }

  handlePageshow(event) {
    if (event.persisted) {
      this.resetToContent()
    }
  }

  resetToContent() {
    this.contentTargets.forEach((el) => el.classList.remove("hidden"))
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add("hidden")
    }
    this.syncLoginSubmit()
  }
}
